import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import { ROUTES } from 'config/navConfig';
import Button from 'components/ui/Button';
import { useRole } from 'context/RoleContext';
import { IC_STATES, getICStateConfig } from 'config/workflowStates';

const DEAL_STRUCTURES = [
  {
    id: 'deal-1',
    name: 'Solano Wind Farm',
    linkedOpportunity: 'Solano Wind Farm — Execution',
    structureType: 'Senior Equity',
    totalDealSize: 85,
    equityTranche: 34,
    equityPct: 40,
    debtTranche: 51,
    debtPct: 60,
    targetIRR: 12.4,
    hurdleRate: 8,
    carry: 20,
    status: 'Approved',
    lastModified: '2026-02-28',
    icState: IC_STATES.APPROVED,
  },
  {
    id: 'deal-2',
    name: 'Porto Digital Hub',
    linkedOpportunity: 'Porto Digital Hub — Asset Management',
    structureType: 'Blended',
    totalDealSize: 34,
    equityTranche: 20.4,
    equityPct: 60,
    debtTranche: 13.6,
    debtPct: 40,
    targetIRR: 16.8,
    hurdleRate: 9,
    carry: 20,
    status: 'Approved',
    lastModified: '2026-02-20',
    icState: IC_STATES.APPROVED_WITH_CONDITIONS,
  },
  {
    id: 'deal-3',
    name: 'Elbe Biomass Plant',
    linkedOpportunity: 'Elbe Biomass Plant — Screening',
    structureType: 'Club Deal',
    totalDealSize: 57,
    equityTranche: 22.8,
    equityPct: 40,
    debtTranche: 34.2,
    debtPct: 60,
    targetIRR: 11.3,
    hurdleRate: 7,
    carry: 17.5,
    status: 'In Review',
    lastModified: '2026-03-01',
    icState: IC_STATES.UNDER_REVIEW,
  },
  {
    id: 'deal-4',
    name: 'Lisbon Metro Extension',
    linkedOpportunity: 'Lisbon Metro Extension — Screening',
    structureType: 'Mezzanine',
    totalDealSize: 94,
    equityTranche: 28.2,
    equityPct: 30,
    debtTranche: 65.8,
    debtPct: 70,
    targetIRR: 10.1,
    hurdleRate: 7.5,
    carry: 15,
    status: 'In Review',
    lastModified: '2026-03-02',
    icState: IC_STATES.SUBMITTED,
  },
  {
    id: 'deal-5',
    name: 'Nordic Fibre Network Phase II',
    linkedOpportunity: 'Nordic Fibre Network Phase II — Diligence',
    structureType: 'Co-Investment',
    totalDealSize: 41,
    equityTranche: 24.6,
    equityPct: 60,
    debtTranche: 16.4,
    debtPct: 40,
    targetIRR: 14.9,
    hurdleRate: 8.5,
    carry: 17.5,
    status: 'Draft',
    lastModified: '2026-02-15',
    icState: IC_STATES.DRAFT,
  },
];

const STATUS_CONFIG = {
  Draft: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  'In Review': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  Approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Archived: { bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-300' },
};

const STRUCTURE_TYPE_CONFIG = {
  'Senior Equity': 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  Mezzanine: 'bg-indigo-50 text-indigo-700',
  Blended: 'bg-teal-50 text-teal-700',
  'Club Deal': 'bg-purple-50 text-purple-700',
  'Co-Investment': 'bg-cyan-50 text-cyan-700',
};

function formatRelativeDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

export default function DealStructuringHub() {
  const navigate = useNavigate();
  const { can } = useRole();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortField, setSortField] = useState('lastModified');
  const [sortDir, setSortDir] = useState('desc');
  const [openActionsId, setOpenActionsId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const filtered = useMemo(() => {
    let list = [...DEAL_STRUCTURES];
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(
        (d) =>
          d?.name?.toLowerCase()?.includes(q) ||
          d?.linkedOpportunity?.toLowerCase()?.includes(q) ||
          d?.structureType?.toLowerCase()?.includes(q)
      );
    }
    if (statusFilter !== 'All') {
      list = list?.filter((d) => d?.status === statusFilter);
    }
    list?.sort((a, b) => {
      let av = a?.[sortField];
      let bv = b?.[sortField];
      if (typeof av === 'string') av = av?.toLowerCase();
      if (typeof bv === 'string') bv = bv?.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [searchQuery, statusFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleDuplicate = (deal) => {
    setOpenActionsId(null);
    showToast(`"${deal?.name}" duplicated successfully`);
  };

  const handleArchive = (deal) => {
    setOpenActionsId(null);
    showToast(`"${deal?.name}" archived`, 'info');
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Icon name="ChevronsUpDown" size={14} color="var(--color-muted-foreground)" />;
    return sortDir === 'asc'
      ? <Icon name="ChevronUp" size={14} color="var(--color-primary)" />
      : <Icon name="ChevronDown" size={14} color="var(--color-primary)" />;
  };

  const stats = [
    { label: 'Active Structures', value: '4', icon: 'Layers', color: 'text-[#3b4c4c]' },
    { label: 'Pending IC Approval', value: '2', icon: 'Clock', color: 'text-amber-600' },
    { label: 'Approved This Quarter', value: '3', icon: 'CheckCircle', color: 'text-emerald-600' },
    { label: 'Total Deal Value Structured', value: '£284m', icon: 'TrendingUp', color: 'text-navy-600' },
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="px-6 md:px-10 lg:px-12 py-8">

          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Deal Structuring</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Capital structure, waterfall mechanics, and term sheet generation —{' '}
                  <span className="italic">Sample data, for demonstration purposes only</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                {can('canSubmitIC') && (
                  <Button
                    variant="outline"
                    size="md"
                    iconName="ClipboardCheck"
                    iconPosition="left"
                    onClick={() => navigate(ROUTES.icSubmission)}
                  >
                    IC Submissions
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => navigate(ROUTES.dealWorkspace)}
                >
                  Create Structure
                </Button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <div className="relative flex-1 max-w-sm">
                <Icon
                  name="Search"
                  size={16}
                  color="var(--color-muted-foreground)"
                  className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search deals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {['All', 'Draft', 'In Review', 'Approved', 'Archived']?.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {stats?.map((stat) => (
              <div key={stat?.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 shadow-sm">
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-muted`}>
                  <Icon name={stat?.icon} size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <p className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{stat?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {[
                      { label: 'Deal Name', field: 'name' },
                      { label: 'Linked Opportunity', field: 'linkedOpportunity' },
                      { label: 'Structure Type', field: 'structureType' },
                      { label: 'Total Size', field: 'totalDealSize' },
                      { label: 'Equity Tranche', field: 'equityTranche' },
                      { label: 'Debt Tranche', field: 'debtTranche' },
                      { label: 'Target IRR', field: 'targetIRR' },
                      { label: 'Hurdle', field: 'hurdleRate' },
                      { label: 'Carry', field: 'carry' },
                      { label: 'Status', field: 'status' },
                      { label: 'Last Modified', field: 'lastModified' },
                      { label: 'Actions', field: null },
                    ]?.map((col) => (
                      <th
                        key={col?.label}
                        className={`px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${
                          col?.field ? 'cursor-pointer hover:text-foreground select-none' : ''
                        }`}
                        onClick={() => col?.field && handleSort(col?.field)}
                      >
                        <span className="flex items-center gap-1">
                          {col?.label}
                          {col?.field && <SortIcon field={col?.field} />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered?.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-4 py-12 text-center text-muted-foreground">
                        No deal structures match your filters.
                      </td>
                    </tr>
                  ) : (
                    filtered?.map((deal) => {
                      const statusCfg = STATUS_CONFIG?.[deal?.status] || STATUS_CONFIG?.Draft;
                      const typeCfg = STRUCTURE_TYPE_CONFIG?.[deal?.structureType] || 'bg-slate-100 text-slate-600';
                      return (
                        <tr key={deal?.id} className="hover:bg-muted/30 transition-colors">
                          {/* Deal Name */}
                          <td className="px-4 py-3">
                            <button
                              onClick={() => navigate(`/deal-structuring-workspace?dealId=${deal?.id}`)}
                              className="font-semibold text-foreground hover:text-primary transition-colors text-left"
                            >
                              {deal?.name}
                            </button>
                          </td>
                          {/* Linked Opportunity */}
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[rgba(59,76,76,0.07)] text-blue-700 text-xs font-medium border border-blue-100">
                              <Icon name="Link" size={10} />
                              {deal?.linkedOpportunity}
                            </span>
                          </td>
                          {/* Structure Type */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${typeCfg}`}>
                              {deal?.structureType}
                            </span>
                          </td>
                          {/* Total Deal Size */}
                          <td className="px-4 py-3 font-semibold text-foreground whitespace-nowrap">
                            £{deal?.totalDealSize}m
                          </td>
                          {/* Equity Tranche */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-foreground font-medium">£{deal?.equityTranche}m</span>
                            <span className="text-muted-foreground text-xs ml-1">({deal?.equityPct}%)</span>
                          </td>
                          {/* Debt Tranche */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-foreground font-medium">£{deal?.debtTranche}m</span>
                            <span className="text-muted-foreground text-xs ml-1">({deal?.debtPct}%)</span>
                          </td>
                          {/* Target IRR */}
                          <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                            {deal?.targetIRR}%
                          </td>
                          {/* Hurdle */}
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                            {deal?.hurdleRate}%
                          </td>
                          {/* Carry */}
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                            {deal?.carry}%
                          </td>
                          {/* Status + IC State */}
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg?.bg} ${statusCfg?.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${statusCfg?.dot}`} />
                                {deal?.status}
                              </span>
                              {deal?.icState && (() => {
                                const icCfg = getICStateConfig(deal.icState);
                                return (
                                  <button
                                    onClick={() => navigate(ROUTES.icSubmission)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-caption font-medium transition-opacity hover:opacity-70"
                                    style={{ color: icCfg.color, background: icCfg.bgColor }}
                                    title="View IC Submission"
                                  >
                                    <Icon name="ClipboardCheck" size={10} />
                                    IC: {icCfg.label}
                                  </button>
                                );
                              })()}
                            </div>
                          </td>
                          {/* Last Modified */}
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                            {formatRelativeDate(deal?.lastModified)}
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="relative">
                              <button
                                onClick={() => setOpenActionsId(openActionsId === deal?.id ? null : deal?.id)}
                                className="flex items-center gap-1 px-2 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-xs font-medium"
                              >
                                <Icon name="MoreHorizontal" size={16} />
                              </button>
                              {openActionsId === deal?.id && (
                                <div
                                  className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-20 py-1"
                                  onMouseLeave={() => setOpenActionsId(null)}
                                >
                                  <button
                                    onClick={() => { navigate(`/deal-structuring-workspace?dealId=${deal?.id}`); setOpenActionsId(null); }}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                                  >
                                    <Icon name="ArrowRight" size={14} />
                                    Open Workspace
                                  </button>
                                  <button
                                    onClick={() => handleDuplicate(deal)}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                                  >
                                    <Icon name="Copy" size={14} />
                                    Duplicate
                                  </button>
                                  <button
                                    onClick={() => handleArchive(deal)}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Icon name="Archive" size={14} />
                                    Archive
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-border bg-muted/20 text-xs text-muted-foreground">
              Showing {filtered?.length} of {DEAL_STRUCTURES?.length} deal structures — Sample data, for demonstration purposes only
            </div>
          </div>
        </div>
      </div>
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast?.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'
        }`}>
          <Icon name={toast?.type === 'success' ? 'CheckCircle' : 'Info'} size={16} />
          {toast?.message}
        </div>
      )}
    </AppLayout>
  );
}
